import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { StatusPill, type PillStyle } from "@/components/ui/StatusPill";
import { Popover } from "@/components/ui/Popover";
import { Icon } from "@/components/ui/Icon";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import type { ViewStatusOption } from "@/lib/boardView";

interface StatusCellProps {
  value: string | null;
  options: ViewStatusOption[];
  rowH?: number;
  pillStyle?: PillStyle;
  onChange?: (id: string) => void;
  /** Disables the picker popover. Pill renders display-only. */
  readOnly?: boolean;
  /** Board-admin-only "+ Neue Option" inline-create. When provided the
   * button mints a new option via the column-config PATCH. */
  onAddOption?: (label: string) => Promise<{ id: string; label: string; color: string } | null>;
  canCreateOption?: boolean;
}

export function StatusCell({
  value,
  options,
  rowH = 44,
  pillStyle = "tint",
  onChange,
  readOnly = false,
  onAddOption,
  canCreateOption = false,
}: StatusCellProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const opt = options.find((o) => o.id === value);
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null);
  const newInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (creatingOpen) {
      const id = window.setTimeout(() => newInputRef.current?.focus(), 16);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [creatingOpen]);

  useEffect(() => {
    if (!open) {
      setCreatingOpen(false);
      setNewLabel("");
      setJustCreatedId(null);
    }
  }, [open]);

  const showCreator = !readOnly && canCreateOption && typeof onAddOption === "function";

  const submitNewOption = async () => {
    if (!onAddOption) return;
    const label = newLabel.trim();
    if (!label) return;
    setSubmitting(true);
    try {
      const created = await onAddOption(label);
      if (created) {
        setNewLabel("");
        setJustCreatedId(created.id);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onCreatorKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void submitNewOption();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setCreatingOpen(false);
      setNewLabel("");
    }
  };
  return (
    <>
      <CellWrap
        rowH={rowH}
        onClick={readOnly ? undefined : () => setOpen(true)}
        interactive={!readOnly}
        pad="0 10px"
      >
        <div ref={ref} style={{ width: "100%" }}>
          {opt ? (
            <StatusPill option={opt} pillStyle={pillStyle} onClick={() => undefined} />
          ) : (
            <span style={{ fontSize: 12, color: "var(--ink-4)" }}>—</span>
          )}
        </div>
      </CellWrap>
      {!readOnly && open ? (
        <Popover anchor={ref.current} onClose={() => setOpen(false)} width={200}>
          <div style={{ padding: 8 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                padding: "6px 8px 8px",
              }}
            >
              {t.setStatus}
            </div>
            {options
              .filter((o) => !o.archived)
              .map((o) => {
                const isJustCreated = justCreatedId === o.id;
                return (
                  <button
                    key={o.id}
                    onClick={() => {
                      onChange?.(o.id);
                      setOpen(false);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = isJustCreated
                        ? "var(--surface-soft)"
                        : "transparent")
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 8px",
                      width: "100%",
                      border: "none",
                      background: isJustCreated ? "var(--surface-soft)" : "transparent",
                      borderRadius: 6,
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: isJustCreated ? `inset 0 0 0 1px ${BRAND.blue}` : "none",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
                      <StatusPill option={o} pillStyle={pillStyle} maxWidth="100%" />
                    </div>
                    {value === o.id ? (
                      <Icon
                        name="check"
                        size={14}
                        color="var(--accent)"
                        style={{ flexShrink: 0 }}
                      />
                    ) : null}
                  </button>
                );
              })}
            {showCreator ? (
              <>
                <div style={{ height: 1, background: "var(--hairline)", margin: "6px 0" }}></div>
                {creatingOpen ? (
                  <div
                    style={{
                      padding: "4px 8px 6px",
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    <input
                      ref={newInputRef}
                      type="text"
                      name="new-status-option"
                      autoComplete="off"
                      value={newLabel}
                      placeholder={t.newOption}
                      disabled={submitting}
                      onChange={(e) => setNewLabel(e.target.value)}
                      onKeyDown={onCreatorKey}
                      style={{
                        flex: 1,
                        // Inputs have an intrinsic min-width (~170px) that overflows
                        // the 200px popover unless explicitly allowed to shrink.
                        minWidth: 0,
                        padding: "6px 8px",
                        fontSize: 12.5,
                        border: "1px solid var(--hairline-strong)",
                        borderRadius: 6,
                        background: "var(--surface)",
                        color: "var(--ink-1)",
                        outline: "none",
                        fontFamily: "inherit",
                      }}
                    />
                    <button
                      onClick={() => void submitNewOption()}
                      disabled={submitting || !newLabel.trim()}
                      title={t.confirm}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 26,
                        height: 26,
                        flexShrink: 0,
                        border: "1px solid var(--hairline-strong)",
                        borderRadius: 6,
                        background: "var(--surface)",
                        cursor: submitting || !newLabel.trim() ? "default" : "pointer",
                        opacity: submitting || !newLabel.trim() ? 0.45 : 1,
                        padding: 0,
                      }}
                    >
                      <Icon name="check" size={14} color="var(--accent)" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setCreatingOpen(true)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 8px",
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      borderRadius: 6,
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: 12.5,
                      color: "var(--ink-2)",
                    }}
                  >
                    <Icon name="plus" size={14} /> {t.newOption}
                  </button>
                )}
              </>
            ) : null}
          </div>
        </Popover>
      ) : null}
    </>
  );
}
